import { useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { ensurePushSubscription } from "../lib/webPush";
import { readGoalPrefsLocal } from "./GoalAlertSettings";
import { LIVE_EVENT_NAME } from "../lib/liveEventsSocket";
import { formatLiveMinute } from "../lib/liveMinute";

/**
 * Iter 98 — LivePushNotifier
 *
 * Mounts once at the App root.  When the user is logged in:
 *   1. Fetches their followed-teams list
 *   2. Every 60s, polls /live/now-playing
 *   3. If a followed team appears in the live list AND we haven't
 *      already alerted for that fixture, fires a browser notification
 *
 * No external push gateway, no VAPID keys.  Works as long as the
 * user has the tab open. (For background pushes, a future iter could
 * register a service worker + Web Push.)
 */
const LivePushNotifier = () => {
  const { token, user } = useAuth();
  const followedRef = useRef(new Set());
  const alertedRef = useRef(new Set()); // fixture_ids we've already notified about kickoff
  const lastScoresRef = useRef(new Map()); // fixture_id → { home: int, away: int }
  const lastFetchRef = useRef(0);

  useEffect(() => {
    if (!token || !user) {
      followedRef.current = new Set();
      alertedRef.current = new Set();
      lastScoresRef.current = new Map();
      return;
    }

    // Fetch followed teams once on auth + refresh every 5 minutes
    const fetchFollowed = async () => {
      try {
        const r = await axios.get(`${API}/me/followed-teams`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        followedRef.current = new Set((r.data?.teams || []).map((t) => t.team_id));
      } catch {
        // silent — auth issues, keep last list
      }
    };
    fetchFollowed();
    const followedInterval = setInterval(fetchFollowed, 5 * 60 * 1000);

    // Iter 100 — also register Web Push subscription so the user gets
    // alerts even when this tab is closed.  Silent no-op on unsupported
    // browsers / when Notification permission isn't yet granted.
    ensurePushSubscription(token).catch(() => {});

    // Polling loop — every 60s
    const tick = async () => {
      if (followedRef.current.size === 0) return;
      if ("Notification" in window && Notification.permission !== "granted") return;
      const now = Date.now();
      if (now - lastFetchRef.current < 50_000) return; // safety throttle
      lastFetchRef.current = now;

      try {
        const r = await axios.get(`${API}/live/now-playing`, { timeout: 15000 });
        const liveMatches = r.data?.matches || [];
        const seenThisPoll = new Set();
        for (const m of liveMatches) {
          const fid = m.fixture_id;
          if (!fid) continue;
          seenThisPoll.add(fid);
          const homeId = m.home_team?.id;
          const awayId = m.away_team?.id;
          const isFollowed =
            (homeId && followedRef.current.has(homeId)) ||
            (awayId && followedRef.current.has(awayId));
          if (!isFollowed) continue;

          const homeName = m.home_team?.name || "Home";
          const awayName = m.away_team?.name || "Away";
          const league = m.league?.name || "";
          const status = m.status || "";
          const elapsed = formatLiveMinute(m.minute, m.minute_extra, status) || status;
          const score = m.score || {};
          const homeGoals = Number.isFinite(score.home) ? score.home : 0;
          const awayGoals = Number.isFinite(score.away) ? score.away : 0;
          const followedHome = homeId && followedRef.current.has(homeId);
          const followedSide = followedHome ? homeName : awayName;

          // ── 1. Kickoff notification (once per fixture)
          if (!alertedRef.current.has(fid)) {
            alertedRef.current.add(fid);
            try {
              new Notification(`${followedSide} are playing now`, {
                body: `${homeName} vs ${awayName}\n${league} · ${status}`,
                icon: m.home_team?.logo || "/ndakubet-logo.jpg",
                tag: `live-${fid}`,
                requireInteraction: false,
              });
            } catch {
              // silent
            }
          }

          // ── 2. Goal notifications (delta vs previous poll)
          const prev = lastScoresRef.current.get(fid);
          if (prev) {
            const homeDelta = homeGoals - prev.home;
            const awayDelta = awayGoals - prev.away;
            // Only fire on positive deltas (ignore VAR-disallowed goals which decrement)
            if (homeDelta > 0 || awayDelta > 0) {
              const scorerSide = homeDelta > 0 ? "home" : "away";
              const scorerTeam = scorerSide === "home" ? homeName : awayName;
              const scoreLine = `${homeName} ${homeGoals}-${awayGoals} ${awayName}`;
              const myTeamScored =
                (scorerSide === "home" && followedHome) ||
                (scorerSide === "away" && !followedHome);
              const headline = myTeamScored
                ? `⚽ GOAL — ${followedSide}!`
                : `⚠ ${scorerTeam} scored against ${followedSide}`;
              // Iter 109 — Respect Goal-alert master + push toggles.
              const prefs = readGoalPrefsLocal();
              const allowGoalPush =
                prefs.goal_alerts_enabled && prefs.goal_push_enabled;
              if (allowGoalPush) {
                try {
                  new Notification(headline, {
                    body: `${scoreLine}${elapsed ? ` (${elapsed})` : ""}\n${league}`,
                    icon:
                      (scorerSide === "home" ? m.home_team?.logo : m.away_team?.logo) ||
                      "/ndakubet-logo.jpg",
                    // tag uses score so multiple goals in one match each fire
                    tag: `goal-${fid}-${homeGoals}-${awayGoals}`,
                    requireInteraction: false,
                  });
                } catch {
                  // silent
                }
              }
            }
          }
          lastScoresRef.current.set(fid, { home: homeGoals, away: awayGoals });
        }

        // Garbage-collect score state for matches that ended
        for (const fid of Array.from(lastScoresRef.current.keys())) {
          if (!seenThisPoll.has(fid)) {
            lastScoresRef.current.delete(fid);
          }
        }
      } catch {
        // silent — network blip, retry next tick
      }
    };

    const liveInterval = setInterval(tick, 60_000);
    // Fire one tick shortly after mount so the user gets immediate coverage
    const kickoff = setTimeout(tick, 8_000);

    // Iter 110 — Sub-second WebSocket path.  When a goal event arrives
    // via the broker, fire the notification immediately AND advance
    // `lastScoresRef` so the next 60s poll-tick treats it as already
    // delivered (no duplicate notification).
    const onLiveEvent = (e) => {
      const ev = e?.detail;
      if (!ev || ev._synthetic) return;
      if (ev.type !== "goal") return;
      const fid = ev.fixture_id;
      if (!fid) return;
      const homeId = ev.home_team?.id;
      const awayId = ev.away_team?.id;
      const isFollowed =
        (homeId && followedRef.current.has(homeId)) ||
        (awayId && followedRef.current.has(awayId));
      if (!isFollowed) {
        // Still update state so the polling loop doesn't fire a duplicate later.
        lastScoresRef.current.set(fid, {
          home: ev.score?.home || 0,
          away: ev.score?.away || 0,
        });
        return;
      }

      if ("Notification" in window && Notification.permission !== "granted") return;
      const prefs = readGoalPrefsLocal();
      if (!prefs.goal_alerts_enabled || !prefs.goal_push_enabled) return;

      const homeName = ev.home_team?.name || "Home";
      const awayName = ev.away_team?.name || "Away";
      const homeGoals = ev.score?.home || 0;
      const awayGoals = ev.score?.away || 0;
      const followedHome = !!(homeId && followedRef.current.has(homeId));
      const followedSide = followedHome ? homeName : awayName;
      const scorerSide = ev.scorer_side || (homeGoals > (ev.prev_score?.home || 0) ? "home" : "away");
      const scorerTeam = scorerSide === "home" ? homeName : awayName;
      const myTeamScored =
        (scorerSide === "home" && followedHome) ||
        (scorerSide === "away" && !followedHome);
      const headline = myTeamScored
        ? `⚽ GOAL — ${followedSide}!`
        : `⚠ ${scorerTeam} scored against ${followedSide}`;
      const elapsed = formatLiveMinute(ev.minute, ev.minute_extra, ev.status) || ev.status || "";
      const scoreLine = `${homeName} ${homeGoals}-${awayGoals} ${awayName}`;

      try {
        new Notification(headline, {
          body: `${scoreLine}${elapsed ? ` (${elapsed})` : ""}\n${ev.league?.name || ""}`,
          icon:
            (scorerSide === "home" ? ev.home_team?.logo : ev.away_team?.logo) ||
            "/ndakubet-logo.jpg",
          // Same tag scheme as the polling path → browser de-dups cross-channel.
          tag: `goal-${fid}-${homeGoals}-${awayGoals}`,
          requireInteraction: false,
        });
      } catch {
        // silent
      }
      lastScoresRef.current.set(fid, { home: homeGoals, away: awayGoals });
    };
    window.addEventListener(LIVE_EVENT_NAME, onLiveEvent);

    return () => {
      clearInterval(liveInterval);
      clearInterval(followedInterval);
      clearTimeout(kickoff);
      window.removeEventListener(LIVE_EVENT_NAME, onLiveEvent);
    };
  }, [token, user]);

  return null;
};

export default LivePushNotifier;
